import { Alert, Button, Card, CardBody, CardHeader, Spinner } from '@hvac/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  captureFilename,
  captureStore,
  toFile,
  type PendingCapture,
} from '../../shared/upload/capture-store.js';
import type { UploadItem } from '../../shared/upload/upload-manager.js';
import { useUploadQueue } from '../documents/use-upload-queue.js';

/**
 * Capture: the page a technician opens from the home screen in the truck.
 *
 * One big button opens the rear camera. Each photo is written to the browser's
 * storage before anything else, then sent when there is signal; closing the
 * app loses nothing, and opening it again sends what is waiting. The page says
 * what it has done in plain counts: waiting, sending, sent today.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export function CapturePage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const organizationName =
    session.organizations.find((entry) => entry.organization.id === organizationId)?.organization
      .name ?? '';
  const queue = useUploadQueue({ organizationId, source: 'web_mobile' });
  const [pending, setPending] = useState<PendingCapture[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  // Which stored captures have been handed to the uploader, by upload item id.
  const handed = useRef(new Map<string, string>());
  const [inFlightFromStore, setInFlightFromStore] = useState(0);

  const refreshPending = useCallback(() => {
    captureStore
      .list(organizationId)
      .then(setPending)
      .catch(() =>
        setError(
          'The phone would not let this page store photos. Nothing is lost while the page stays open.',
        ),
      );
  }, [organizationId]);

  // Hand every stored photo not yet in flight to the uploader. Called on open,
  // when the connection returns, and after each new photo.
  const drain = useCallback(async () => {
    if (!navigator.onLine) return;
    const waiting = await captureStore.list(organizationId);
    const fresh = waiting.filter((capture) => ![...handed.current.values()].includes(capture.id));
    if (fresh.length === 0) return;
    const items = queue.add(fresh.map(toFile));
    items.forEach((item, index) => {
      const capture = fresh[index];
      if (capture !== undefined) handed.current.set(item.id, capture.id);
    });
    setInFlightFromStore(handed.current.size);
  }, [organizationId, queue]);

  useEffect(() => {
    refreshPending();
    void drain();
  }, [refreshPending, drain]);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      void drain();
    };
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [drain]);

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  // A completed upload releases its stored photo; a duplicate is already in
  // the books, so it is released too. Failures stay stored for the next try.
  useEffect(() => {
    for (const item of queue.items) {
      const captureId = handed.current.get(item.id);
      if (captureId === undefined) continue;
      if (item.status === 'completed' || item.status === 'duplicate') {
        handed.current.delete(item.id);
        setInFlightFromStore(handed.current.size);
        void captureStore.remove(captureId).then(refreshPending);
      } else if (item.status === 'failed' || item.status === 'cancelled') {
        handed.current.delete(item.id);
        setInFlightFromStore(handed.current.size);
      }
    }
  }, [queue.items, refreshPending]);

  const take = async (files: readonly File[]) => {
    const now = new Date();
    for (const file of files) {
      const mimeType = file.type === '' ? 'image/jpeg' : file.type;
      const capture: PendingCapture = {
        id: crypto.randomUUID(),
        organizationId,
        filename:
          file.name === '' || file.name === 'image.jpg'
            ? captureFilename(now, mimeType)
            : file.name,
        mimeType,
        byteSize: file.size,
        blob: file,
        capturedAt: now.toISOString(),
      };
      try {
        await captureStore.put(capture);
      } catch {
        setError(
          'The phone would not store the photo. It will still be sent while this page stays open.',
        );
        queue.add([file]);
        continue;
      }
    }
    refreshPending();
    void drain();
  };

  const inFlight = queue.items.filter((item) =>
    ['queued', 'hashing', 'requesting', 'uploading', 'finalising'].includes(item.status),
  );
  const failed = queue.items.filter((item) => item.status === 'failed');
  const sentToday = queue.items.filter((item) => item.status === 'completed').length;
  const duplicates = queue.items.filter((item) => item.status === 'duplicate').length;
  const waitingForSignal = Math.max(0, pending.length - inFlightFromStore);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Capture</h1>
        <p className="text-sm text-content-muted">
          {organizationName}. Photograph the receipt; it becomes a record in the books.
        </p>
      </div>

      {error === null ? null : <Alert tone="warning">{error}</Alert>}
      {online ? null : (
        <Alert tone="info" title="No signal">
          Keep taking photos. They are kept on this phone and sent the next time you open this page
          with a connection.
        </Alert>
      )}

      <button
        type="button"
        onClick={() => cameraInput.current?.click()}
        className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-[var(--radius-control)] bg-brand-600 text-white shadow-lg active:bg-brand-700"
      >
        <span className="text-4xl" aria-hidden="true">
          📷
        </span>
        <span className="text-lg font-semibold">Take a photo of a receipt</span>
        <span className="text-xs text-white/80">Rear camera. One receipt per photo.</span>
      </button>
      <Button variant="secondary" onClick={() => libraryInput.current?.click()}>
        Choose from the photo library
      </Button>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(event) => {
          void take([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
      <input
        ref={libraryInput}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="sr-only"
        onChange={(event) => {
          void take([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />

      <Card>
        <CardHeader
          title="Today"
          description={
            waitingForSignal === 0 &&
            inFlight.length === 0 &&
            failed.length === 0 &&
            sentToday === 0
              ? 'Nothing yet.'
              : [
                  sentToday > 0 ? `${String(sentToday)} sent` : null,
                  duplicates > 0 ? `${String(duplicates)} already in the books` : null,
                  inFlight.length > 0 ? `${String(inFlight.length)} sending` : null,
                  waitingForSignal > 0 ? `${String(waitingForSignal)} waiting for signal` : null,
                  failed.length > 0 ? `${String(failed.length)} failed` : null,
                ]
                  .filter((part) => part !== null)
                  .join(' · ')
          }
        />
        {inFlight.length === 0 && failed.length === 0 && waitingForSignal === 0 ? null : (
          <CardBody className="flex flex-col gap-2 text-sm">
            {inFlight.map((item) => (
              <Row key={item.id} item={item} />
            ))}
            {failed.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-2">
                <span className="truncate text-danger">
                  {item.filename}: {item.error ?? 'failed'}
                </span>
                <Button size="sm" variant="secondary" onClick={() => queue.retry(item.id)}>
                  Try again
                </Button>
              </div>
            ))}
            {waitingForSignal > 0 ? (
              <p className="text-content-muted">
                {String(waitingForSignal)} photo{waitingForSignal === 1 ? '' : 's'} kept on this
                phone until there is signal.
              </p>
            ) : null}
          </CardBody>
        )}
      </Card>

      <InstallHint prompt={installPrompt} onInstalled={() => setInstallPrompt(null)} />
    </div>
  );
}

function Row({ item }: { item: UploadItem }) {
  return (
    <div className="flex items-center gap-2">
      <Spinner className="size-4 text-brand-600" label={null} />
      <span className="truncate">{item.filename}</span>
      <span className="ml-auto tabular text-content-muted">
        {String(Math.round(item.progress * 100))}%
      </span>
    </div>
  );
}

function InstallHint({
  prompt,
  onInstalled,
}: {
  prompt: BeforeInstallPromptEvent | null;
  onInstalled: () => void;
}) {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (standalone) return null;
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <Card>
      <CardHeader
        title="Put it on the home screen"
        description={
          prompt !== null
            ? 'Opens straight to the camera, and works with no signal.'
            : ios
              ? 'In Safari, tap Share, then "Add to Home Screen". It opens straight to the camera and works with no signal.'
              : 'In the browser menu, choose "Install app" or "Add to Home Screen". It opens straight to the camera and works with no signal.'
        }
      />
      {prompt === null ? null : (
        <CardBody>
          <Button
            onClick={() => {
              void prompt
                .prompt()
                .then(() => prompt.userChoice)
                .then((choice) => {
                  if (choice.outcome === 'accepted') onInstalled();
                });
            }}
          >
            Install
          </Button>
        </CardBody>
      )}
    </Card>
  );
}
